import { FC, ReactElement, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import { DialogMenu, SelectBox } from "@/components";
import { filter_black_icon } from "@/constants";
import { useNavigate } from "react-router-dom";
import { tokens } from "@/tokens";

export interface IFilter {
  onFiltersChange: (val: BasicFilters) => Promise<void>;
  onClose: () => void;
  anchorEl: Element | null;
  initialFilterState: BasicFilters;
  partnerData: SPOwnerPartnerResponse | undefined;
}

const FilterPopover: FC<IFilter> = ({
  onFiltersChange,
  onClose,
  anchorEl,
  initialFilterState,
  partnerData,
}): ReactElement => {
  const [filters, setFilters] = useState(initialFilterState);
  const [filterLoading, setFilterLoading] = useState(false);
  const navigate = useNavigate();

  const applyFilters = async (): Promise<void> => {
    setFilterLoading(true);
    try {
      await onFiltersChange(filters);
    } catch (error) {
    } finally {
      setFilterLoading(false);
      onClose();
    }
  };

  const resetFilters = (): void => {
    setFilters(initialFilterState);
    onFiltersChange(initialFilterState);
    navigate("/clients");
    onClose();
  };

  const handleChangeValue =
    (name: keyof BasicFilters) =>
    (value: string): void => {
      setFilters({
        ...filters,
        [name]: value,
      });
    };

  return (
    <DialogMenu
      id="advance-filter-menu"
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={2.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <img src={filter_black_icon} alt="filter" />
          <Typography
            variant="h5"
            fontSize={15}
            fontWeight={tokens.fontWeight.bold}
            color={tokens.text.primary}
          >
            Filter
          </Typography>
          <Box
            sx={{
              width: 24,
              height: 24,
              display: "grid",
              placeContent: "center",
              backgroundColor: "rgba(11,73,75,0.12)",
              color: tokens.brand.primary,
              borderRadius: "50%",
            }}
          >
            <Typography variant="caption" fontWeight={tokens.fontWeight.semibold} color={tokens.brand.primary}>
              {Object.values(filters).filter(Boolean).length}
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            color: tokens.brand.primary,
            textDecorationLine: "underline",
            cursor: "pointer",
          }}
          variant="body2"
          fontWeight={tokens.fontWeight.semibold}
          onClick={resetFilters}
        >
          Clear All
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ p: 2 }} spacing={1}>
        <SelectBox
          label="Partner"
          placeholder="Select Partner"
          value={filters.partner}
          handleChangeValue={handleChangeValue("partner")}
          options={
            partnerData?.data.map((partner) => ({
              label: partner.firstName + " " + partner.lastName,
              value: partner._id,
            })) || []
          }
        />
        <SelectBox
          label="Status"
          placeholder="Select Status"
          value={filters.status}
          handleChangeValue={handleChangeValue("status")}
          options={[
            { label: "Active", value: "active" },
            { label: "Restricted", value: "restricted" },
          ]}
        />
        {/* <DatePicker
          onChangeValue={handleChangeValue("dateAdded")}
          value={filters.dateAdded}
          label="Date Added"
        /> */}
      </Stack>
      <Divider />
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        p={2}
        sx={{
          background: tokens.bg.muted,
        }}
      >
        <Button
          variant="outlined"
          sx={{
            flex: 1,
            borderColor: tokens.border.default,
            color: tokens.text.body,
            borderRadius: tokens.radius.md,
            height: 34,
            fontSize: tokens.fontSize.md,
            boxShadow: "none",
            '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            flex: 1,
            gap: 1,
            bgcolor: tokens.brand.primary,
            borderRadius: tokens.radius.md,
            height: 34,
            fontSize: tokens.fontSize.md,
            boxShadow: "none",
            '&:hover': { bgcolor: tokens.brand.primaryHover },
          }}
          onClick={applyFilters}
          disabled={filterLoading}
          startIcon={filterLoading ? <CircularProgress size={20} /> : null}
        >
          {filterLoading ? "Applying" : "Apply"}
        </Button>
      </Stack>
    </DialogMenu>
  );
};

export default FilterPopover;
